import { Label, Slider, Checkbox } from '@creanizer/ui'
import type { WorldBuilderInput } from '@creanizer/types'

interface GenerationOptionsFormProps {
  input: WorldBuilderInput
  onChange: (input: WorldBuilderInput) => void
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
}

export function GenerationOptionsForm({ input, onChange, useStreaming = false, onStreamingChange }: GenerationOptionsFormProps) {
  return (
    <div className="space-y-4">
      <h4 className="font-semibold">生成オプション</h4>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label>詳細度</Label>
          <span className="text-sm font-medium">{input.detailLevel || 5}/10</span>
        </div>
        <Slider
          value={[input.detailLevel || 5]}
          onValueChange={([value]) => onChange({ ...input, detailLevel: value })}
          min={1}
          max={10}
          step={1}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">
          高いほど詳細な世界観が生成されます
        </p>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label>創造性レベル</Label>
          <span className="text-sm font-medium">{input.creativityLevel || 7}/10</span>
        </div>
        <Slider
          value={[input.creativityLevel || 7]}
          onValueChange={([value]) => onChange({ ...input, creativityLevel: value })}
          min={1}
          max={10}
          step={1}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">
          高いほど創造的で予測不可能な要素が増えます
        </p>
      </div>
      
      <div className="space-y-3">
        <Label>含める要素</Label>
        
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="includeTimeline"
            checked={input.includeTimeline ?? true}
            onCheckedChange={(checked) => onChange({ ...input, includeTimeline: !!checked })}
          />
          <span className="text-sm">歴史年表</span>
        </label>
        
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="includeCultures"
            checked={input.includeCultures ?? true}
            onCheckedChange={(checked) => onChange({ ...input, includeCultures: !!checked })}
          />
          <span className="text-sm">文化設定</span>
        </label>
        
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="includeGeography"
            checked={input.includeGeography ?? true}
            onCheckedChange={(checked) => onChange({ ...input, includeGeography: !!checked })}
          />
          <span className="text-sm">地理設定</span>
        </label>
        
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="includeMagicSystem"
            checked={input.includeMagicSystem ?? false}
            onCheckedChange={(checked) => onChange({ ...input, includeMagicSystem: !!checked })}
          />
          <span className="text-sm">魔法体系</span>
        </label>
        
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="includeTechnology"
            checked={input.includeTechnology ?? false}
            onCheckedChange={(checked) => onChange({ ...input, includeTechnology: !!checked })}
          />
          <span className="text-sm">技術体系</span>
        </label>
      </div>
      
      <div className="space-y-2">
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="useProMode"
            checked={input.useProMode ?? false}
            onCheckedChange={(checked) => onChange({ ...input, useProMode: !!checked })}
          />
          <span className="text-sm font-medium">Proモード（高品質生成）</span>
        </label>

        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="useGemini3Mode"
            checked={input.useGemini3Mode ?? false}
            onCheckedChange={(checked) => onChange({ ...input, useGemini3Mode: !!checked })}
          />
          <span className="text-sm font-medium">Gemini 3 Pro モード（試験版）</span>
        </label>

        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="useGemini3FlashMode"
            checked={input.useGemini3FlashMode ?? false}
            onCheckedChange={(checked) => onChange({ ...input, useGemini3FlashMode: !!checked })}
          />
          <span className="text-sm font-medium">Gemini 3 Flash モード（試験版）</span>
        </label>

        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            id="useStreaming"
            checked={useStreaming}
            onCheckedChange={(checked) => onStreamingChange?.(!!checked)}
          />
          <span className="text-sm font-medium">ストリーミングモード（リアルタイム生成）</span>
        </label>
        {useStreaming && (
          <p className="text-xs text-muted-foreground ml-6">
            生成中のテキストをリアルタイムで表示します
          </p>
        )}
      </div>
    </div>
  )
}